<template>
  <div class="v2-page">
    <div class="v2-retailer-profile">
      <!-- Loading state -->
      <div
        v-if="!isLoaded || !isCommonDataLoaded"
        class="v2-flex v2-justify-center v2-p-6"
      >
        <V2Spinner />
      </div>

      <!-- Content -->
      <div
        v-else
        class="v2-retailer-profile__layout"
      >
        <!-- Sidebar -->
        <nav class="v2-retailer-profile__sidebar">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            class="v2-retailer-profile__tab-btn"
            :class="{ 'v2-retailer-profile__tab-btn--active': activeTab === tab.key }"
            @click="onTabChange(tab.key)"
          >
            <span
              class="v2-retailer-profile__tab-icon"
              v-html="tab.icon"
            />
            <span class="v2-retailer-profile__tab-label">{{ tab.label }}</span>
          </button>
        </nav>

        <!-- Content panel -->
        <div class="v2-retailer-profile__content">
          <!-- General -->
          <V2ProfileGeneral
            v-if="activeTab === 'general' && profile.general"
            :general-data="profile.general"
            :common-data="countryData"
            @update-general="updateGeneral"
          />

          <!-- Segmentation -->
          <V2ProfileSegmentation
            v-if="activeTab === 'segmentation' && profile.segmentation && Object.values(commons).length"
            :segmentation-data="profile.segmentation"
            :departments="commons.DEPARTMENTS"
            :countries="countryData.COUNTRIES"
            :kingpin-presence-countries="commons.KINGPIN_PRESENCE_COUNTRIES"
            @update-segmentation="updateSegmentation"
          />

          <!-- Documents -->
          <V2ProfileDocument
            v-if="activeTab === 'documents' && profile.documents"
            :document-data="profile.documents"
            @update-document="updateDocument"
          />

          <!-- Addresses -->
          <V2ProfileAddress
            v-if="activeTab === 'addresses'"
            :addresses="addresses"
            :get-user-profile="getUserProfile"
            :common-data="countryData"
          />

          <!-- Social -->
          <V2ProfileSocial
            v-if="activeTab === 'social' && profile.social"
            :social-data="profile.social"
            @update-social="updateSocial"
          />

          <!-- Teammates -->
          <V2ProfileTeammates
            v-if="activeTab === 'teammates'"
            :additional-users="additionalUsers"
            :get-user-profile="getUserProfile"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useRetailerProfile } from '@/composables/useRetailerProfile'
import V2Spinner from '@/components/v2/V2Spinner.vue'
import V2ProfileGeneral from './V2ProfileGeneral.vue'
import V2ProfileSegmentation from './V2ProfileSegmentation.vue'
import V2ProfileDocument from './V2ProfileDocument.vue'
import V2ProfileAddress from './V2ProfileAddress.vue'
import V2ProfileSocial from './V2ProfileSocial.vue'
import V2ProfileTeammates from './V2ProfileTeammates.vue'

export default {
  name: 'V2RetailerProfile',
  components: {
    V2Spinner,
    V2ProfileGeneral,
    V2ProfileSegmentation,
    V2ProfileDocument,
    V2ProfileAddress,
    V2ProfileSocial,
    V2ProfileTeammates,
  },
  setup(props, { root }) {
    const rp = useRetailerProfile()
    rp.init(root.$route.params.tabIndex)

    const tabs = [
      {
        key: 'general',
        label: 'General',
        icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>',
      },
      {
        key: 'segmentation',
        label: 'Segmentation',
        icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>',
      },
      {
        key: 'documents',
        label: 'Commercial Information',
        icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>',
      },
      {
        key: 'addresses',
        label: 'Addresses',
        icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>',
      },
      {
        key: 'social',
        label: 'Social',
        icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>',
      },
      {
        key: 'teammates',
        label: 'Teammates',
        icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>',
      },
    ]

    function onTabChange(tabKey) {
      rp.activeTab.value = tabKey
      const tabTrackMap = {
        general: 'ACCOUNT_SETTING_GENERAL',
        password: 'ACCOUNT_SETTING_PASSWORD',
        segmentation: 'ACCOUNT_SETTING_SEGMENTATION',
        documents: 'ACCOUNT_SETTING_DOCUMENT',
        addresses: 'ACCOUNT_SETTING_ADDRESS',
        social: 'ACCOUNT_SETTING_SOCIAL',
        teammates: 'ACCOUNT_SETTING_ADDITIONAL_USERS',
      }
      rp.trackPageView(tabTrackMap[tabKey])
    }

    return {
      ...rp,
      tabs,
      onTabChange,
    }
  },
}
</script>

<style lang="scss" scoped>
.v2-retailer-profile {
  max-width: 1400px;
  margin: 0 auto;

  &__layout {
    display: grid;
    grid-template-columns: 220px 1fr;
    gap: var(--v2-space-6);
    align-items: flex-start;
  }

  // Glass panel — tab nav container. Same recipe as V2Card variant="panel"
  // so the tab list reads as a discrete frosted card matching the rest of
  // the v2 surfaces.
  &__sidebar {
    background: var(--v2-glass-surface-1);
    backdrop-filter: blur(var(--v2-glass-blur-md)) saturate(180%);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-lg);
    box-shadow: var(--v2-glass-shadow);
    padding: var(--v2-space-2);
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-1);
    position: sticky;
    top: var(--v2-space-4);
  }

  &__tab-btn {
    display: flex;
    align-items: center;
    gap: var(--v2-space-3);
    width: 100%;
    padding: var(--v2-space-2) var(--v2-space-3);
    // Reserve a 1px transparent border on the inactive state so switching
    // tabs doesn't change the box dimensions when the active state's
    // border appears — prevents the layout-shift "shake" on click.
    border: 1px solid transparent;
    border-radius: var(--v2-radius-md);
    background: transparent;
    color: var(--v2-muted-foreground);
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    font-weight: 500;
    // Pre-allocate the bold metrics so the text width doesn't change when
    // the active state bumps font-weight to 600. Both inactive and active
    // tabs occupy the bold-text width; the lighter weight just renders
    // thinner glyphs in the same space.
    font-variation-settings: 'wght' 500;
    text-align: left;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;

    &:hover {
      background: oklch(1 0 0 / 0.06);
      color: var(--v2-foreground);
    }

    // Light glassmorphic active state — primary-violet tinted glass.
    // Same recipe PLG's sidebar uses for active nav items
    // (oklch 0.62 0.21 295 at 14%/28% alpha) so b2b's tab nav matches
    // the PLG nav pattern when both are visible side by side.
    &--active {
      background: oklch(0.62 0.21 295 / 0.14);
      backdrop-filter: blur(var(--v2-glass-blur-md)) saturate(180%);
      border-color: oklch(0.62 0.21 295 / 0.28);
      color: var(--v2-foreground);
      font-variation-settings: 'wght' 600;
      box-shadow: 0 1px 0 0 oklch(1 0 0 / 0.08) inset;

      &:hover {
        background: oklch(0.62 0.21 295 / 0.14);
        color: var(--v2-foreground);
      }
    }
  }

  &__tab-icon {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }

  &__tab-label {
    line-height: 1.3;
  }

  &__content {
    min-width: 0;
  }

  @media (max-width: 768px) {
    &__layout {
      grid-template-columns: 1fr;
    }

    &__sidebar {
      position: static;
      flex-direction: row;
      flex-wrap: wrap;
      gap: var(--v2-space-1);
    }

    &__tab-btn {
      flex: 0 0 auto;
      padding: var(--v2-space-2);
    }

    &__tab-label {
      display: none;
    }
  }
}
</style>
